import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject, catchError, of, tap, throwError } from 'rxjs';

import { EntryPoint, EntryPointService, JobSiteService, PageFilter } from '@mirage-projects/cw-commons';
import { DeleteJobSiteOptions, Job, JobSite, JobSiteCreationData, JobSiteReferenceData, ResultPage } from '@mirage-projects/cw-models';

@Injectable({
    providedIn: 'root',
})
export class AppJobSiteService implements JobSiteService {
    constructor(
        private http: HttpClient,
        private entryPointService: EntryPointService
    ) {}
    private jobSitesUrl = this.entryPointService.getResource(EntryPoint.JOB_SITES);

    private updateJobSiteSubject$ = new Subject<JobSite>();
    updateJobSite$ = this.updateJobSiteSubject$.asObservable();

    /**
     * Add project to a job site
     * @param jobSiteId Job site id
     * @param projectId Project id
     */
    addProject(jobSiteId: number, projectId: number): Observable<JobSite> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES, `${jobSiteId}/projects`);

        return this.http.post<JobSite>(`${url}`, { project: projectId }).pipe(
            tap((jobSite: JobSite) => {
                this.updateJobSiteSubject$.next(jobSite);
            }),
            catchError((error) => throwError(() => error))
        );
    }

    /**
     * Add layer to a job site
     * @param jobSiteId Job site id
     * @param layer layer to be added to the job site
     */
    addReferenceData(jobSiteId: number, layer: JobSiteReferenceData): Observable<JobSite> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES, `${jobSiteId}/workflows/import-reference-data`);

        return this.http.post<JobSite>(`${url}`, layer);
    }

    /**
     * Get snapshot type options
     */
    snapshotTypeOptions(): { label: string; value: string }[] {
        return [
            {
                label: 'GROUND_SURFACE',
                value: 'Ground surface',
            },
            {
                label: '3D_OBJECT',
                value: '3D objects',
            },
        ];
    }

    /**
     * Update job site information
     * @param jobSiteId Site id to update
     * @param jobSiteData Job site data
     */
    updateJobSite(jobSiteId: number, jobSiteData: Partial<JobSite>): Observable<JobSite> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES, `${jobSiteId}`);
        return this.http.patch<JobSite>(`${url}`, jobSiteData).pipe(
            tap((jobSite: JobSite) => {
                this.updateJobSiteSubject$.next(jobSite);
            }),
            catchError((error) => throwError(() => error))
        );
    }

    /**
     * Remove a specific project from a job site
     * @param jobSiteId Job site id
     * @param projectId Project id
     */
    removeProject(jobSiteId: number, projectId: number): Observable<void> {
        console.warn('SiteService.removeProject Not implemented yet', { jobSiteId, projectId });
        return of();
    }

    /**
     * Get job site by id
     * @param id Job site id
     */
    get(id: number): Observable<JobSite> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES, `${id}`);
        return this.http.get<JobSite>(`${url}`);
    }

    /**
     * Get the list of Job sites
     * @param params Parameters to filter the list
     * @returns
     */
    getJobSites(filters: PageFilter): Observable<ResultPage<JobSite>> {
        const config = { params: filters.getQueryParams() };
        return this.http.get<ResultPage<JobSite>>(`${this.jobSitesUrl}`, config).pipe(
            catchError((err: any) => {
                return throwError(() => err);
            })
        );
    }

    /**
     * Create a new site
     * @param jobSiteData Required data to create a site
     * @returns
     */
    createJobSite(jobSiteData: JobSiteCreationData): Observable<JobSite> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES);

        return this.http.post<JobSite>(`${url}`, jobSiteData);
    }

    /**
     * Get job site by id
     * @param id Job site id
     */
    getJobsFromJobSite(id: number): Observable<Job[]> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES, `${id}/jobs`);
        return this.http.get<Job[]>(`${url}`);
    }

    /**
     * Delete a job site
     * @param jobSiteId Id of the jobsite to delete
     * @param options  Deleting options
     */
    deleteJobSite(jobSiteId: number, options: DeleteJobSiteOptions): Observable<void> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES, `${jobSiteId}`);

        if (!url) {
            throw new Error('Invalid entry point');
        }

        const {
            removeChildProjects: projects = false,
            removeLayers: layers = false,
            removeGCPFiles: gpc = false,
            removeDesignDataFiles: design_data = false,
        } = options;

        const params = new HttpParams({ fromObject: { projects, layers, gpc, design_data } });

        return this.http.delete<void>(url, { params }).pipe(
            catchError(() => {
                return throwError(() => new Error('Error deleting the site'));
            })
        );
    }

    dettachProjects(jobSiteId: number, projectsIdsList: number[]): Observable<void> {
        const url = this.entryPointService.getResource(EntryPoint.JOB_SITES, `${jobSiteId}/projects/dettach-projects`);
        const data = { projectIds: projectsIdsList };
        return this.http.post<void>(`${url}`, data);
    }
}
